import { useEffect, useMemo, useRef, useState } from "react";
import { describeCoreError } from "../../core-api/errors";
import type { OperationsContext } from "../../core-contracts/operations";
import {
  createEmailTemplate,
  deleteEmailTemplate,
  loadEmailTemplates,
  updateEmailTemplate,
  type EmailTemplate,
} from "../cases/api";
import {
  CaseRichTextEditor,
  type CaseRichTextEditorHandle,
} from "../cases/CaseRichTextEditor";
import {
  changePersonalPassword,
  changePersonalTags,
  loadPasswordStatus,
  loadPersonalSignature,
  loadPersonalTags,
  savePersonalSignature,
  type PasswordStatus,
  type PersonalSignature,
  type PersonalTag,
} from "./api";
import "../cases/cases.css";
import "./personal-settings.css";

type Panel = "password" | "templates" | "signature" | "tags";
const panels: Array<[Panel, string, string]> = [
  ["password", "修改密码", "更新当前账号的登录密码"],
  ["templates", "个人邮件模板", "当前工作空间中的个人模板"],
  ["signature", "个性签名", "当前工作空间中的个人签名"],
  ["tags", "个人标签", "当前工作空间中的案件标签"],
];

export function generatePersonalPassword(length = 24) {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  for (;;) {
    let result = "";
    while (result.length < length) {
      for (const value of crypto.getRandomValues(new Uint8Array(32))) {
        if (value < 248) result += alphabet[value % alphabet.length];
        if (result.length === length) break;
      }
    }
    if (/[A-Z]/.test(result) && /[a-z]/.test(result) && /[0-9]/.test(result)) return result;
  }
}

export function PersonalSettings({
  workspaceName,
  permissions,
}: {
  workspaceName: string;
  permissions: OperationsContext["permissions"];
}) {
  const [panel, setPanel] = useState<Panel>("password");
  return (
    <section className="personal-settings">
      <header className="personal-settings-header">
        <div>
          <h1>个人设置</h1>
          <p>管理账号安全，以及你在“{workspaceName}”中的邮件和案件偏好。</p>
        </div>
      </header>
      <div className="personal-settings-layout">
        <nav aria-label="个人设置菜单">
          {panels.map(([key, label, description]) => (
            <button
              key={key}
              type="button"
              className={panel === key ? "active" : ""}
              onClick={() => setPanel(key)}
            >
              <strong>{label}</strong>
              <small>{description}</small>
            </button>
          ))}
        </nav>
        <main>
          {panel === "password" && <PasswordPanel />}
          {panel === "templates" && (
            <TemplatePanel allowed={permissions.caseExternalMessageSend ?? false} />
          )}
          {panel === "signature" && (
            <SignaturePanel allowed={permissions.caseExternalMessageSend ?? false} />
          )}
          {panel === "tags" && <TagPanel allowed={permissions.caseTagManage ?? false} />}
        </main>
      </div>
    </section>
  );
}

function PasswordPanel() {
  const [status, setStatus] = useState<PasswordStatus>();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    loadPasswordStatus(controller.signal).then(setStatus).catch((cause) => {
      if (!controller.signal.aborted) setError(describeCoreError(cause));
    });
    return () => controller.abort();
  }, []);
  const valid =
    newPassword.length >= 20 &&
    /[A-Z]/.test(newPassword) &&
    /[a-z]/.test(newPassword) &&
    /[0-9]/.test(newPassword) &&
    !/\s/.test(newPassword) &&
    newPassword === repeatPassword;
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await changePersonalPassword({ currentPassword, newPassword, repeatPassword });
      setStatus({ available: true, passwordRotatedAt: result.passwordRotatedAt });
      setCurrentPassword("");
      setNewPassword("");
      setRepeatPassword("");
      setNotice("密码已更新，其他已登录设备已退出。当前页面可以继续使用。");
    } catch (cause) {
      setError(describeCoreError(cause));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="personal-panel">
      <div className="personal-panel-heading">
        <div><h2>修改密码</h2><p>至少 20 位，必须包含大写字母、小写字母和数字，不能包含空格。</p></div>
        {status?.passwordRotatedAt && <small>上次修改：{new Date(status.passwordRotatedAt).toLocaleString("zh-CN")}</small>}
      </div>
      {status && !status.available ? (
        <div className="personal-empty">当前账号未启用密码登录，请联系管理员设置初始密码。</div>
      ) : (
        <form className="personal-form narrow" onSubmit={submit}>
          <label>当前密码<input type="password" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required /></label>
          <label>新密码<input type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={20} /></label>
          <label>重复新密码<input type="password" autoComplete="new-password" value={repeatPassword} onChange={(e) => setRepeatPassword(e.target.value)} required minLength={20} /></label>
          <div className="personal-actions">
            <button type="button" onClick={() => { const value = generatePersonalPassword(); setNewPassword(value); setRepeatPassword(value); void navigator.clipboard?.writeText(value); }}>生成安全密码</button>
            <button className="primary" disabled={busy || !currentPassword || !valid}>{busy ? "正在保存…" : "保存新密码"}</button>
          </div>
        </form>
      )}
      <Feedback error={error} notice={notice} />
    </section>
  );
}

function TemplatePanel({ allowed }: { allowed: boolean }) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const refresh = async () => setTemplates(await loadEmailTemplates());
  useEffect(() => { if (allowed) void refresh().catch((cause) => setError(describeCoreError(cause))); }, [allowed]);
  const filtered = useMemo(() => templates.filter((item) => `${item.name} ${item.subject}`.toLowerCase().includes(search.trim().toLowerCase())), [templates, search]);
  function select(item?: EmailTemplate) {
    setSelectedId(item?.id ?? ""); setName(item?.name ?? ""); setSubject(item?.subject ?? ""); setHtml(item?.htmlBody ?? ""); setError(""); setNotice("");
  }
  async function save() {
    if (!name.trim() || !html.trim() || busy) return;
    setBusy(true); setError(""); setNotice("");
    try {
      const result = selectedId
        ? await updateEmailTemplate(selectedId, { name, subject, html })
        : await createEmailTemplate({ name, subject, html });
      await refresh(); select(result); setNotice(selectedId ? "模板已更新" : "模板已创建");
    } catch (cause) { setError(describeCoreError(cause)); } finally { setBusy(false); }
  }
  async function remove() {
    if (!selectedId || busy || !window.confirm("确认删除这个个人邮件模板？")) return;
    setBusy(true); setError("");
    try { await deleteEmailTemplate(selectedId); await refresh(); select(); setNotice("模板已删除"); }
    catch (cause) { setError(describeCoreError(cause)); } finally { setBusy(false); }
  }
  if (!allowed) return <PermissionEmpty text="你没有发送案件对外消息的权限，无法管理个人邮件模板。" />;
  return (
    <section className="personal-panel">
      <div className="personal-panel-heading"><div><h2>个人邮件模板</h2><p>模板仅供你在当前工作空间使用，可在案件发信界面直接载入。</p></div><button onClick={() => select()}>新建模板</button></div>
      <div className="template-list-tools"><input aria-label="搜索邮件模板" placeholder="搜索名称或主题" value={search} onChange={(e) => setSearch(e.target.value)} /></div>
      <div className="template-list">
        {filtered.map((item) => <button key={item.id} className={selectedId === item.id ? "active" : ""} onClick={() => select(item)}><strong>{item.name}</strong><span>{item.subject || "无主题"}</span><small>{new Date(item.updatedAt).toLocaleString("zh-CN")}</small></button>)}
        {!filtered.length && <div className="personal-empty">还没有个人邮件模板</div>}
      </div>
      <div className="personal-form template-editor">
        <label>模板名称<input maxLength={120} value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label>邮件主题<input maxLength={998} value={subject} onChange={(e) => setSubject(e.target.value)} /></label>
        <RichEditorField label="模板正文" html={html} onChange={setHtml} resetKey={`${selectedId}-${templates.length}`} />
        <div className="personal-actions"><button className="primary" disabled={busy || !name.trim() || !html.trim()} onClick={() => void save()}>{busy ? "正在保存…" : selectedId ? "保存修改" : "创建模板"}</button>{selectedId && <button className="danger" disabled={busy} onClick={() => void remove()}>删除模板</button>}</div>
      </div>
      <Feedback error={error} notice={notice} />
    </section>
  );
}

function SignaturePanel({ allowed }: { allowed: boolean }) {
  const [signature, setSignature] = useState<PersonalSignature>();
  const [html, setHtml] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [editorRevision, setEditorRevision] = useState(0);
  useEffect(() => {
    if (!allowed) return;
    const controller = new AbortController();
    loadPersonalSignature(controller.signal).then((value) => { setSignature(value); setHtml(value.htmlBody); setEditorRevision((revision) => revision + 1); }).catch((cause) => { if (!controller.signal.aborted) setError(describeCoreError(cause)); });
    return () => controller.abort();
  }, [allowed]);
  async function save() {
    setBusy(true); setError(""); setNotice("");
    try { const value = await savePersonalSignature(html); setSignature(value); setHtml(value.htmlBody); setNotice(value.configured ? "个性签名已保存" : "个性签名已清除"); }
    catch (cause) { setError(describeCoreError(cause)); } finally { setBusy(false); }
  }
  if (!allowed) return <PermissionEmpty text="你没有发送案件对外消息的权限，无法配置个性签名。" />;
  return (
    <section className="personal-panel">
      <div className="personal-panel-heading"><div><h2>个性签名</h2><p>签名按个人和工作空间双重隔离。启用后会附在案件邮件正文底部。</p></div>{signature?.updatedAt && <small>上次修改：{new Date(signature.updatedAt).toLocaleString("zh-CN")}</small>}</div>
      <RichEditorField label="签名内容" html={html} onChange={setHtml} resetKey={`${signature?.version ?? 0}-${editorRevision}`} />
      <div className="personal-actions"><button className="primary" disabled={busy} onClick={() => void save()}>{busy ? "正在保存…" : "保存签名"}</button>{signature?.configured && <button disabled={busy} onClick={() => { setHtml(""); setEditorRevision((revision) => revision + 1); }}>清空内容</button>}</div>
      <Feedback error={error} notice={notice} />
    </section>
  );
}

function TagPanel({ allowed }: { allowed: boolean }) {
  const [tags, setTags] = useState<PersonalTag[]>([]);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState<PersonalTag>();
  const [editName, setEditName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  useEffect(() => { if (allowed) void loadPersonalTags().then((v) => setTags(v.tags)).catch((cause) => setError(describeCoreError(cause))); }, [allowed]);
  async function change(body: Parameters<typeof changePersonalTags>[0]) {
    setBusy(true); setError(""); setNotice("");
    try { const result = await changePersonalTags(body); setTags(result.tags); setName(""); setEditing(undefined); setNotice("个人标签已更新"); }
    catch (cause) { setError(describeCoreError(cause)); } finally { setBusy(false); }
  }
  if (!allowed) return <PermissionEmpty text="你没有管理案件标签的权限，无法维护个人标签。" />;
  return (
    <section className="personal-panel">
      <div className="personal-panel-heading"><div><h2>个人标签</h2><p>标签仅供你在当前工作空间使用，删除后会从相关案件中移除。</p></div></div>
      <form className="tag-create" onSubmit={(e) => { e.preventDefault(); void change({ action: "create", name }); }}><input maxLength={48} value={name} onChange={(e) => setName(e.target.value)} placeholder="输入新标签名称" /><button className="primary" disabled={busy || !name.trim()}>新增标签</button></form>
      <ul className="personal-tag-list">
        {tags.map((tag) => <li key={tag.id}>{editing?.id === tag.id ? <><input autoFocus maxLength={48} value={editName} onChange={(e) => setEditName(e.target.value)} /><button disabled={busy || !editName.trim()} onClick={() => void change({ action: "rename", tagId: tag.id, name: editName, expectedVersion: tag.version })}>保存</button><button onClick={() => setEditing(undefined)}>取消</button></> : <><div><strong>{tag.name}</strong><small>已用于 {tag.usageCount} 个案件</small></div><button onClick={() => { setEditing(tag); setEditName(tag.name); }}>修改</button><button className="danger" disabled={busy} onClick={() => { if (window.confirm(`删除标签“${tag.name}”？`)) void change({ action: "delete", tagId: tag.id, expectedVersion: tag.version }); }}>删除</button></>}</li>)}
        {!tags.length && <li className="personal-empty">还没有个人标签</li>}
      </ul>
      <Feedback error={error} notice={notice} />
    </section>
  );
}

function RichEditorField({ label, html, onChange, resetKey }: { label: string; html: string; onChange: (html: string) => void; resetKey: string | number }) {
  const editor = useRef<CaseRichTextEditorHandle>(null);
  useEffect(() => { const frame = requestAnimationFrame(() => editor.current?.setHtml(html)); return () => cancelAnimationFrame(frame); }, [resetKey]);
  return <div className="personal-rich-field"><span>{label}</span><CaseRichTextEditor ref={editor} ariaLabel={label} resetKey={resetKey} onChange={(snapshot) => onChange(snapshot.html)} /></div>;
}
function PermissionEmpty({ text }: { text: string }) { return <section className="personal-panel"><div className="personal-empty">{text}</div></section>; }
function Feedback({ error, notice }: { error: string; notice: string }) { return <>{error && <p role="alert">{error}</p>}{notice && <p role="status">{notice}</p>}</>; }
